'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import { useToast } from '@/components/ui/ToastContainer'
import { useUI } from '@/contexts/UIContext'
import { useUser } from '@/contexts/UserContext'
import { DemoOnboardingTour } from '@/components/onboarding/DemoOnboardingTour'
import { useDeferredStartup } from '@/hooks/useDeferredStartup'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const ADMIN_FOOTER_BADGE_POLL_MS = 300000

// Per-platform brand colors (matches layout.css :root variables)
const PLATFORM_COLORS: Record<string, string> = {
  'customer-service': '#0284c7',
  'depo-magaza': '#2563eb',
  'dizayn': '#9333ea',
  'erp': '#06b6d4',
  'farktor': '#4f46e5',
  'lojistik': '#0d9488',
  'report': '#0891b2',
  'store': '#1d4ed8',
  'virtual-market': '#7c3aed',
  'web': '#059669',
}

function getPlatformColor(platform: unknown): string {
  const key = String(platform || '').toLowerCase().replace(/\s+/g, '-')
  return PLATFORM_COLORS[key] || '#6366f1'
}

function isBrowserTabVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

function normalizeNavPath(pathname: string) {
  if (!pathname || pathname === '/') return '/'
  const [path] = String(pathname).split('?')
  return path.endsWith('/') ? path.slice(0, -1) : path
}

function getPanelPathAliases(pathname: string) {
  const normalizedPath = normalizeNavPath(pathname)
  const aliases = new Set<string>([normalizedPath])

  const aliasMap: Record<string, string[]> = {
    '/pages/report': ['/report', '/report-raporlar'],
    '/report': ['/pages/report', '/report-raporlar'],
    '/report-raporlar': ['/pages/report', '/report'],
  }

  for (const alias of aliasMap[normalizedPath] || []) {
    aliases.add(normalizeNavPath(alias))
  }

  return aliases
}

export interface NavItem {
  id: number
  top_id: number | null
  platform: string
  name: string
  desc?: string
  url: string
  path?: string
  icon: string
  sort: number
  nav: number
  language?: any[]
  log?: any
  project?: any[]
}

interface Platform {
  id?: number
  platform: string
  name?: string
  icon?: string
}

type PlatformLike = Platform | string

type UserTaskSummaryPage = {
  pageUrl: string
  pageTitle: string
  openCount: number
  totalCount: number
}

function getPlatformValue(platform: PlatformLike): string {
  if (typeof platform === 'string') return platform
  return String(platform?.platform || platform?.name || '')
}

interface TodoItem {
  id: number
  text: string
  completed: boolean
}

interface Attachment {
  id: number
  type: 'image' | 'file' | 'voice'
  name: string
  url: string
  size?: number
  duration?: number // for voice notes
}

interface SharedUser {
  id: number
  name: string
  avatar?: string
}

interface Note {
  id: number
  title: string
  content: string
  updatedAt: Date
  createdAt: Date
  color: string
  pinned: boolean
  deleted: boolean
  deletedAt?: Date
  todos: TodoItem[]
  attachments: Attachment[]
  sharedWith: SharedUser[]
}

interface SidebarProps {
  collapsed?: boolean
  nav?: NavItem[]
  platforms?: PlatformLike[]
  currentPlatform?: string
  onStartTour?: () => void
}

export function Sidebar({ collapsed = false, nav = [], platforms = [], currentPlatform = '', onStartTour }: SidebarProps) {
  const pathname = usePathname()
  const router = useRouter()
  const { showToast } = useToast()
  const { refreshUser, user } = useUser()
  const deferredStartupReady = useDeferredStartup()
  const isAdminUser = Number(user?.admin || 0) === 1
  const { mobileSidebarOpen, setMobileSidebarOpen, globalLoading } = useUI()
  const [isPlatformOpen, setIsPlatformOpen] = useState(false)
  const [tourStartSignal, setTourStartSignal] = useState(0)
  const [isPlatformPinned, setIsPlatformPinned] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('platformPinned') === 'true'
  })
  const [isChangingPlatform, setIsChangingPlatform] = useState(false)
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null)
  const [isClient, setIsClient] = useState(false)
  const [taskSummaryPages, setTaskSummaryPages] = useState<UserTaskSummaryPage[]>([])
  const [workTaskBadgeCount, setWorkTaskBadgeCount] = useState(0)
  const [testRequestBadgeCount, setTestRequestBadgeCount] = useState(0)
  const [infoRequestBadgeCount, setInfoRequestBadgeCount] = useState(0)
  const [setupStatus, setSetupStatus] = useState({
    completedSteps: 0,
    totalSteps: 25,
    progressPercent: 0,
  })
  const headerRef = useRef<HTMLDivElement>(null)
  
  // Notes Panel State
  const [isNotesOpen, setIsNotesOpen] = useState(false)
  const [notesPanelWidth, setNotesPanelWidth] = useState(380)
  const [isResizing, setIsResizing] = useState(false)
  const [notes, setNotes] = useState<Note[]>([
    { id: 1, title: 'İlk Notum', content: 'Bu bir örnek nottur.', updatedAt: new Date(), createdAt: new Date(), color: '#fafafa', pinned: false, deleted: false, todos: [], attachments: [], sharedWith: [] },
    { id: 2, title: 'Yapılacaklar', content: 'Bugünkü görevler', updatedAt: new Date(Date.now() - 86400000), createdAt: new Date(Date.now() - 86400000), color: '#fff3cd', pinned: true, deleted: false, todos: [{ id: 1, text: 'Toplantı', completed: false }, { id: 2, text: 'Rapor hazırla', completed: false }, { id: 3, text: 'Mail gönder', completed: true }], attachments: [], sharedWith: [{ id: 1, name: 'Ahmet', avatar: '' }] },
  ])
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)
  const [editingNote, setEditingNote] = useState<{title: string, content: string} | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showTrash, setShowTrash] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [newTodoText, setNewTodoText] = useState('')
  
  // Mock team members for sharing
  const teamMembers: SharedUser[] = [
    { id: 1, name: 'Ahmet Yılmaz', avatar: '' },
    { id: 2, name: 'Ayşe Demir', avatar: '' },
    { id: 3, name: 'Mehmet Kaya', avatar: '' },
    { id: 4, name: 'Fatma Şahin', avatar: '' },
    { id: 5, name: 'Can Öztürk', avatar: '' },
  ]
  
  // Color options
  const noteColors = [
    { name: 'Beyaz', value: '#fafafa' },
    { name: 'Sarı', value: '#fff3cd' },
    { name: 'Yeşil', value: '#d4edda' },
    { name: 'Mavi', value: '#cce5ff' },
    { name: 'Mor', value: '#e2d9f3' },
    { name: 'Pembe', value: '#f8d7da' },
    { name: 'Turuncu', value: '#ffe5d0' },
  ]

  useEffect(() => {
    if (!deferredStartupReady || !isAdminUser || !user?.firm_id || !user?.firm_store_id) return

    let cancelled = false
    const loadSetupStatus = async () => {
      try {
        const params = new URLSearchParams({
          firm_id: String(user.firm_id),
          store_id: String(user.firm_store_id),
        })
        const token = localStorage.getItem('auth_token') || ''
        const response = await fetch(`/api/setup/status?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const data = await response.json().catch(() => null)
        if (cancelled || !response.ok || !data?.success) return
        setSetupStatus({
          completedSteps: Number(data.data?.completed_steps || 0),
          totalSteps: Number(data.data?.total_steps || 25),
          progressPercent: Number(data.data?.progress_percent || 0),
        })
      } catch (error) {
        console.error('[Sidebar] setup status failed:', error)
      }
    }

    loadSetupStatus()
    window.addEventListener('panel:setup-status-changed', loadSetupStatus)
    return () => {
      cancelled = true
      window.removeEventListener('panel:setup-status-changed', loadSetupStatus)
    }
  }, [deferredStartupReady, isAdminUser, user?.firm_id, user?.firm_store_id])

  // Resize handler for notes panel
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    
    const startX = e.clientX
    const startWidth = notesPanelWidth
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX)
      setNotesPanelWidth(Math.min(600, Math.max(200, newWidth)))
    }
    
    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
  }
  
  const handleMouseEnter = (e: React.MouseEvent, text: string) => {
    if (!collapsed) return
    const rect = e.currentTarget.getBoundingClientRect()
    setTooltip({
      text,
      x: rect.right + 8,
      y: rect.top + rect.height / 2
    })
  }

  const handleMouseLeave = () => {
    setTooltip(null)
  }

  useEffect(() => {
    setIsClient(true)
  }, [])

  const isFarktorPersonnel = isAdminUser
  const isFarktorDeveloper = isFarktorPersonnel && Number(user?.is_developer || 0) === 1
  const isFarktorSupport = isFarktorPersonnel && (Number(user?.is_support_team || 0) === 1 || Number(user?.is_manager || 0) === 1)

  const fetchTaskSummary = useCallback(async () => {
    if (!user) return
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const response = await fetch(`${API_BASE}/user/tasks/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data?.success) {
        setTaskSummaryPages(Array.isArray(data.data?.pages) ? data.data.pages : [])
      }
    } catch (error) {
      console.error('user task summary fetch error:', error)
    }
  }, [user])

  useEffect(() => {
    if (!deferredStartupReady) return
    fetchTaskSummary()
    const handler = () => fetchTaskSummary()
    window.addEventListener('user-tasks:changed', handler)
    return () => window.removeEventListener('user-tasks:changed', handler)
  }, [deferredStartupReady, fetchTaskSummary])

  const fetchAdminFooterBadges = useCallback(async () => {
    const canFetchWorkTasks = Number(user?.admin || 0) === 1 && Number(user?.is_developer || 0) === 1
    const canFetchTests = Number(user?.admin || 0) === 1 && (Number(user?.is_support_team || 0) === 1 || Number(user?.is_manager || 0) === 1)
    if (!canFetchWorkTasks && !canFetchTests) return
    const token = localStorage.getItem('auth_token')
    if (!token) return
    try {
      const headers = { Authorization: `Bearer ${token}` }
      const [workResponse, testResponse, infoResponse] = await Promise.all([
        canFetchWorkTasks ? fetch(`${API_BASE}/tasks/me/summary`, { headers }) : Promise.resolve(null),
        canFetchTests ? fetch(`${API_BASE}/test-requests/summary`, { headers }) : Promise.resolve(null),
        canFetchTests ? fetch(`${API_BASE}/call-center/info-requests/summary`, { headers }) : Promise.resolve(null),
      ])
      const [workData, testData, infoData] = await Promise.all([
        workResponse ? workResponse.json().catch(() => ({})) : Promise.resolve({}),
        testResponse ? testResponse.json().catch(() => ({})) : Promise.resolve({}),
        infoResponse ? infoResponse.json().catch(() => ({})) : Promise.resolve({}),
      ])
      if (workResponse?.ok && workData?.success) {
        setWorkTaskBadgeCount(Number(workData.data?.openCount || 0))
      }
      if (testResponse?.ok && testData?.success) setTestRequestBadgeCount(Number(testData.data?.openCount || 0))
      if (infoResponse?.ok && infoData?.success) setInfoRequestBadgeCount(Number(infoData.data?.openCount || 0))
    } catch (error) {
      console.error('admin footer badge fetch error:', error)
    }
  }, [user])

  useEffect(() => {
    if (!deferredStartupReady) return
    fetchAdminFooterBadges()
    const handler = () => fetchAdminFooterBadges()
    const pollAdminFooterBadges = () => {
      if (isBrowserTabVisible()) fetchAdminFooterBadges()
    }
    const handleVisibilityChange = () => {
      if (isBrowserTabVisible()) fetchAdminFooterBadges()
    }
    window.addEventListener('work-tasks:changed', handler)
    window.addEventListener('test-requests:changed', handler)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const interval = window.setInterval(pollAdminFooterBadges, ADMIN_FOOTER_BADGE_POLL_MS)
    return () => {
      window.removeEventListener('work-tasks:changed', handler)
      window.removeEventListener('test-requests:changed', handler)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.clearInterval(interval)
    }
  }, [deferredStartupReady, fetchAdminFooterBadges])

  // Sync pin state to localStorage; toggle body class based on pin state
  useEffect(() => {
    localStorage.setItem('platformPinned', String(isPlatformPinned))
    if (isPlatformPinned) {
      document.body.classList.add('panel-has-pinned-bar')
    } else {
      document.body.classList.remove('panel-has-pinned-bar')
    }
    return () => document.body.classList.remove('panel-has-pinned-bar')
  }, [isPlatformPinned])

  // Close platform dropdown on outside click
  useEffect(() => {
    if (!isPlatformOpen) return
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('.demo-onboarding-tour')) return
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setIsPlatformOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isPlatformOpen])

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [pathname])

  // Platform değiştirme fonksiyonu
  const handlePlatformChange = async (platformName: string) => {
    if (platformName === currentPlatform || isChangingPlatform) return
    
    setIsChangingPlatform(true)
    setIsPlatformOpen(false)
    
    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('https://app.canerozen.com/api/auth/change-platform', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          platform: platformName,
          store_scope: localStorage.getItem('admin_store_nav_scope') === '1' ? '1' : '0',
        })
      })

      if (response.ok) {
        showToast('Platform başarıyla değiştirildi', 'success')
        await refreshUser()
        if (pathname !== '/') {
          router.push('/')
        }
      } else {
        const errorData = await response.json()
        showToast(errorData.message || 'Platform değiştirme başarısız', 'error')
      }
    } catch (error) {
      console.error('Platform change error:', error)
      showToast('Bir hata oluştu', 'error')
    } finally {
      setIsChangingPlatform(false)
    }
  }

  // Notes functions
  const handleCreateNote = () => {
    const newNote: Note = {
      id: Date.now(),
      title: 'Yeni Not',
      content: '',
      updatedAt: new Date(),
      createdAt: new Date(),
      color: '#fafafa',
      pinned: false,
      deleted: false,
      todos: [],
      attachments: [],
      sharedWith: []
    }
    setNotes([newNote, ...notes])
    setSelectedNote(newNote)
    setEditingNote({ title: newNote.title, content: newNote.content })
    setShowTrash(false)
  }

  const handleDeleteNote = (noteId: number) => {
    // Move to trash instead of permanent delete
    setNotes(notes.map(n => 
      n.id === noteId 
        ? { ...n, deleted: true, deletedAt: new Date() }
        : n
    ))
    if (selectedNote?.id === noteId) {
      setSelectedNote(null)
      setEditingNote(null)
    }
  }
  
  const handlePermanentDelete = (noteId: number) => {
    setNotes(notes.filter(n => n.id !== noteId))
    if (selectedNote?.id === noteId) {
      setSelectedNote(null)
      setEditingNote(null)
    }
  }
  
  const handleRestoreNote = (noteId: number) => {
    setNotes(notes.map(n => 
      n.id === noteId 
        ? { ...n, deleted: false, deletedAt: undefined }
        : n
    ))
  }
  
  const handleEmptyTrash = () => {
    setNotes(notes.filter(n => !n.deleted))
    setSelectedNote(null)
    setEditingNote(null)
  }

  const handleSaveNote = () => {
    if (!selectedNote || !editingNote) return
    const updatedNote = { 
      ...selectedNote, 
      title: editingNote.title || 'Başlıksız', 
      content: editingNote.content, 
      updatedAt: new Date() 
    }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
  }

  const handleSelectNote = (note: Note) => {
    // Save current note before switching
    if (selectedNote && editingNote) {
      handleSaveNote()
    }
    setSelectedNote(note)
    setEditingNote({ title: note.title, content: note.content })
    setShowColorPicker(false)
  }
  
  const handleTogglePin = (noteId: number) => {
    setNotes(notes.map(n => 
      n.id === noteId ? { ...n, pinned: !n.pinned } : n
    ))
    if (selectedNote?.id === noteId) {
      setSelectedNote({ ...selectedNote, pinned: !selectedNote.pinned })
    }
  }
  
  const handleChangeColor = (noteId: number, color: string) => {
    setNotes(notes.map(n => 
      n.id === noteId ? { ...n, color } : n
    ))
    if (selectedNote?.id === noteId) {
      setSelectedNote({ ...selectedNote, color })
    }
    setShowColorPicker(false)
  }
  
  const handleExportNote = (note: Note) => {
    const content = `# ${note.title}\n\nOluşturulma: ${note.createdAt.toLocaleString('tr-TR')}\nGüncelleme: ${note.updatedAt.toLocaleString('tr-TR')}\n\n---\n\n${note.content}`
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${note.title.replace(/[^a-zA-Z0-9ğüşöçıİĞÜŞÖÇ ]/g, '_')}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }
  
    // Todo functions
  
  // Checkbox insert handler - textarea'ya checkbox ekle
  const insertCheckbox = () => {
    if (!editingNote) return
    const textarea = document.querySelector('textarea[data-notes-editor="true"]') as HTMLTextAreaElement
    if (!textarea) return
    
    const start = textarea.selectionStart
    const fullText = editingNote.title + '\n' + editingNote.content
    
    // Satır başına git
    let lineStart = start
    while (lineStart > 0 && fullText[lineStart - 1] !== '\n') lineStart--
    
    // Checkbox ekle
    const checkboxStr = '☐ '
    const newFullText = fullText.slice(0, lineStart) + checkboxStr + fullText.slice(lineStart)
    const lines = newFullText.split('\n')
    setEditingNote({ title: lines[0], content: lines.slice(1).join('\n') })
    
    // Cursor'u checkbox sonrasına taşı
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(lineStart + checkboxStr.length, lineStart + checkboxStr.length)
    }, 0)
  }

  // Checkbox toggle handler - satırdaki checkbox'ı değiştir
  const toggleCheckboxAtLine = (lineIndex: number) => {
    if (!editingNote) return
    const fullText = editingNote.title + '\n' + editingNote.content
    const lines = fullText.split('\n')
    if (lineIndex >= lines.length) return
    
    const line = lines[lineIndex]
    if (line.startsWith('☐ ')) {
      lines[lineIndex] = '☑ ' + line.slice(2)
    } else if (line.startsWith('☑ ')) {
      lines[lineIndex] = '☐ ' + line.slice(2)
    }
    setEditingNote({ title: lines[0], content: lines.slice(1).join('\n') })
  }

  // Handle Enter key for checkbox continuation
  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return
    if (!editingNote) return
    
    const textarea = e.currentTarget
    const start = textarea.selectionStart
    const fullText = editingNote.title + '\n' + editingNote.content
    
    // Mevcut satırı bul
    let lineStart = start
    while (lineStart > 0 && fullText[lineStart - 1] !== '\n') lineStart--
    const lineEnd = fullText.indexOf('\n', start)
    const currentLine = fullText.slice(lineStart, lineEnd === -1 ? undefined : lineEnd)
    
    // Eğer satır checkbox ile başlıyorsa
    if (currentLine.startsWith('☐ ') || currentLine.startsWith('☑ ')) {
      // Eğer checkbox boşsa (sadece checkbox var), checkbox'ı kaldır
      if (currentLine.trim() === '☐' || currentLine.trim() === '☑') {
        e.preventDefault()
        const newFullText = fullText.slice(0, lineStart) + fullText.slice(lineEnd === -1 ? fullText.length : lineEnd + 1)
        const lines = newFullText.split('\n')
        setEditingNote({ title: lines[0], content: lines.slice(1).join('\n') })
        setTimeout(() => {
          textarea.setSelectionRange(lineStart, lineStart)
        }, 0)
        return
      }
      
      // Yeni satıra checkbox ekle
      e.preventDefault()
      const checkboxStr = '\n☐ '
      const newFullText = fullText.slice(0, start) + checkboxStr + fullText.slice(start)
      const lines = newFullText.split('\n')
      setEditingNote({ title: lines[0], content: lines.slice(1).join('\n') })
      setTimeout(() => {
        textarea.setSelectionRange(start + checkboxStr.length, start + checkboxStr.length)
      }, 0)
    }
  }

  
  const handleAddTodo = () => {
    if (!selectedNote || !newTodoText.trim()) return
    const newTodo: TodoItem = { id: Date.now(), text: newTodoText.trim(), completed: false }
    const updatedNote = { ...selectedNote, todos: [...selectedNote.todos, newTodo], updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
    setNewTodoText('')
  }

  const handleToggleTodo = (todoId: number) => {
    if (!selectedNote) return
    const updatedTodos = selectedNote.todos.map(t => t.id === todoId ? { ...t, completed: !t.completed } : t)
    const updatedNote = { ...selectedNote, todos: updatedTodos, updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
  }

  const handleDeleteTodo = (todoId: number) => {
    if (!selectedNote) return
    const updatedTodos = selectedNote.todos.filter(t => t.id !== todoId)
    const updatedNote = { ...selectedNote, todos: updatedTodos, updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
  }

  const handleToggleShare = (user: SharedUser) => {
    if (!selectedNote) return
    const isShared = selectedNote.sharedWith.some(u => u.id === user.id)
    const updatedShared = isShared ? selectedNote.sharedWith.filter(u => u.id !== user.id) : [...selectedNote.sharedWith, user]
    const updatedNote = { ...selectedNote, sharedWith: updatedShared, updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
  }

  const handleStartRecording = () => {
    setIsRecording(true)
    setRecordingTime(0)
  }

  const handleStopRecording = () => {
    setIsRecording(false)
    if (!selectedNote || recordingTime < 1) return
    const voiceAttachment: Attachment = { id: Date.now(), type: 'voice', name: 'Ses Kaydı', url: '#', duration: recordingTime }
    const updatedNote = { ...selectedNote, attachments: [...selectedNote.attachments, voiceAttachment], updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
    setRecordingTime(0)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedNote || !e.target.files?.length) return
    const file = e.target.files[0]
    const isImage = file.type.startsWith('image/')
    const attachment: Attachment = { id: Date.now(), type: isImage ? 'image' : 'file', name: file.name, url: isImage ? URL.createObjectURL(file) : '#', size: file.size }
    const updatedNote = { ...selectedNote, attachments: [...selectedNote.attachments, attachment], updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
    e.target.value = ''
  }

  const handleTakeScreenshot = () => {
    if (!selectedNote) return
    const screenshotAttachment: Attachment = { id: Date.now(), type: 'image', name: 'Ekran Görüntüsü.png', url: '#', size: 0 }
    const updatedNote = { ...selectedNote, attachments: [...selectedNote.attachments, screenshotAttachment], updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
    alert('Ekran görüntüsü alındı! (Demo)')
  }

  const handleDeleteAttachment = (attachmentId: number) => {
    if (!selectedNote) return
    const updatedAttachments = selectedNote.attachments.filter(a => a.id !== attachmentId)
    const updatedNote = { ...selectedNote, attachments: updatedAttachments, updatedAt: new Date() }
    setNotes(notes.map(n => n.id === selectedNote.id ? updatedNote : n))
    setSelectedNote(updatedNote)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins + ':' + secs.toString().padStart(2, '0')
  }

  // Filter and sort notes
  const filteredNotes = notes
    .filter(n => showTrash ? n.deleted : !n.deleted)
    .filter(n => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return n.title.toLowerCase().includes(query) || n.content.toLowerCase().includes(query)
    })
    .sort((a, b) => {
      // Pinned notes first
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      // Then by date
      return b.updatedAt.getTime() - a.updatedAt.getTime()
    })

  const formatDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) return 'Bugün'
    if (days === 1) return 'Dün'
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
  }

  // Sadece nav=1 ve top_id=0 olan ana menüleri filtrele
  const menuItems = nav.filter(item => item.nav === 1 && item.top_id === 0)
  
  // Recursive olarak tüm parent menüleri bul
  const findAllParentIds = (menuId: number): number[] => {
    const menu = nav.find(item => item.id === menuId)
    if (!menu || !menu.top_id) return []
    return [menu.top_id, ...findAllParentIds(menu.top_id)]
  }

  // Aktif menüyü ve tüm parent menülerini bul
  const getActiveMenuIds = () => {
    const activeIds: number[] = []
    const pathAliases = getPanelPathAliases(pathname)
    
    const matchingMenus = nav
      .filter(item => {
        if (!item.url || item.url === '/') return false
        const menuUrl = normalizeNavPath(item.url)
        for (const pathAlias of pathAliases) {
          if (menuUrl === pathAlias) return true
          if (pathAlias.startsWith(`${menuUrl}/`)) return true
        }
        return false
      })
      .sort((a, b) => (b.url?.length || 0) - (a.url?.length || 0))
    
    const matchedMenu = matchingMenus[0]
    if (matchedMenu) {
      activeIds.push(matchedMenu.id)
      const parentIds = findAllParentIds(matchedMenu.id)
      activeIds.push(...parentIds)
    }
    
    return activeIds
  }

  const activeMenuIds = getActiveMenuIds()

  const taskCountForUrl = useCallback((url?: string) => {
    if (!url) return 0
    const normalizedUrl = url.endsWith('/') && url !== '/' ? url.slice(0, -1) : url
    return taskSummaryPages.reduce((sum, page) => {
      if (!page.openCount) return sum
      const pageUrl = String(page.pageUrl || '')
      const normalizedPageUrl = pageUrl.endsWith('/') && pageUrl !== '/' ? pageUrl.slice(0, -1) : pageUrl
      if (normalizedPageUrl === normalizedUrl || normalizedPageUrl.startsWith(`${normalizedUrl}/`)) {
        return sum + Number(page.openCount || 0)
      }
      return sum
    }, 0)
  }, [taskSummaryPages])

  const renderTaskMarker = (count: number) => {
    if (!count) return null
    return (
      <span className="panel-nav-item__task-marker" title={`${count} açık kişisel görev`}>
        {collapsed ? null : count > 9 ? '9+' : count}
      </span>
    )
  }

  // Icon render fonksiyonu
  const renderIcon = (iconName: string) => {
    if (iconName && iconName.startsWith('fa-')) {
      return <i className={iconName} style={{ fontSize: '16px' }} />
    }
    return (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )
  }

  const navItems = [
    {
      id: 'overview',
      label: 'Ana Sayfa',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      href: '/',
    }
  ]

  const productItems: any[] = []
  const setupSidebarCurrentStep = Math.max(0, setupStatus.completedSteps)
  const setupSidebarTotalSteps = Math.max(1, setupStatus.totalSteps)
  const setupSidebarProgress = Math.max(0, Math.min(100, setupStatus.progressPercent))

  const handleSetupGuideOpen = () => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('panel:setup-guide-open'))
  }

  const handleTourStart = () => {
    setTourStartSignal((value) => value + 1)
    onStartTour?.()
  }

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileSidebarOpen && (
        <div
          className="panel-sidebar-overlay panel-sidebar-overlay--visible"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <aside className={`panel-sidebar ${collapsed ? 'panel-sidebar--collapsed' : ''} ${mobileSidebarOpen ? 'panel-sidebar--mobile-open' : ''}`}>
        {/* Fixed platform bar — always visible */}
        {isClient && platforms.length > 0 && createPortal(
          <div className="panel-pinned-platform-bar">
            {platforms.map((platform, index) => {
              const platformValue = getPlatformValue(platform)
              const platformId = typeof platform === 'string' ? undefined : platform.id
              const isActive = currentPlatform === platformValue
              return (
                <button
                  key={platformId || platformValue || index}
                  className={`panel-pinned-platform-bar__tab ${isActive ? 'panel-pinned-platform-bar__tab--active' : ''}`}
                  onClick={() => handlePlatformChange(platformValue)}
                  disabled={isChangingPlatform}
                  style={isActive ? {
                    background: `${getPlatformColor(platformValue)}25`,
                    color: getPlatformColor(platformValue),
                    borderColor: `${getPlatformColor(platformValue)}55`,
                    boxShadow: `0 0 0 1px ${getPlatformColor(platformValue)}30, 0 2px 8px ${getPlatformColor(platformValue)}20`,
                  } : {}}
                >
                  <span
                    className="panel-pinned-platform-bar__dot"
                    style={{ background: isActive ? getPlatformColor(platformValue) : `${getPlatformColor(platformValue)}70` }}
                  />
                  {platformValue}
                </button>
              )
            })}
          </div>,
          document.body
        )}

        {/* Logo & Platform Selector */}
        <div className="panel-sidebar__header" ref={headerRef}>
          {/* Brand Block — entire area is clickable → opens platform dropdown */}
          <button
            className={`panel-sidebar__brand-btn ${isPlatformOpen ? 'panel-sidebar__brand-btn--open' : ''}`}
            onClick={() => platforms.length > 0 && setIsPlatformOpen(prev => !prev)}
            disabled={isChangingPlatform}
            data-onboarding-target="platform-switcher"
          >
            {/* Logo image — no background, no wrapper styling */}
            <div className={globalLoading ? 'panel-sidebar__logo-icon panel-sidebar__logo-icon--loading' : 'panel-sidebar__logo-icon'}>
              <img src="/images/logo_Icon.svg" alt="Farktör" />
              {globalLoading && <span className="panel-sidebar__logo-loader" aria-hidden="true" />}
            </div>
            {!collapsed && (
              <div className="panel-sidebar__brand-text">
                <span className="panel-sidebar__logo-title">FARKTÖR</span>
                <span className="panel-sidebar__platform-subtitle">
                  <span className="panel-sidebar__platform-subtitle-name">{currentPlatform || 'Platform'}</span>
                  {isChangingPlatform ? (
                    <span className="panel-sidebar__platform-spinner" />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`panel-sidebar__platform-subtitle-arrow ${isPlatformOpen ? 'panel-sidebar__platform-subtitle-arrow--open' : ''}`}
                      width="10"
                      height="10"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  )}
                </span>
              </div>
            )}
          </button>

          {/* Platform Dropdown — includes pin option */}
          {!collapsed && isPlatformOpen && platforms.length > 0 && (
            <div className="panel-platform-dropdown" data-onboarding-target="platform-dropdown">
              <div className="panel-platform-dropdown__label">
                <span>Platform Seç</span>
                <button
                  className={`panel-platform-dropdown__pin ${isPlatformPinned ? 'panel-platform-dropdown__pin--active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setIsPlatformPinned(prev => !prev); }}
                  title={isPlatformPinned ? 'Barı kaldır' : 'Platformları üste sabitle'}
                  data-onboarding-target="platform-pin"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
                  </svg>
                  {isPlatformPinned ? 'Sabitleme' : 'Sabitle'}
                </button>
              </div>
              {platforms.map((platform, index) => {
                const platformValue = getPlatformValue(platform)
                const platformId = typeof platform === 'string' ? undefined : platform.id
                const isActive = currentPlatform === platformValue
                const color = getPlatformColor(platformValue)
                return (
                  <button
                    key={platformId || platformValue || index}
                    className={`panel-platform-dropdown__item ${isActive ? 'panel-platform-dropdown__item--active' : ''}`}
                    onClick={() => handlePlatformChange(platformValue)}
                    disabled={isChangingPlatform}
                    data-onboarding-platform={platformValue}
                    style={isActive ? { background: `${color}18`, color } : {}}
                  >
                    <span className="panel-platform-dropdown__dot" style={{ background: color }} />
                    <span className="panel-platform-dropdown__name">{platformValue}</span>
                    {isActive && (
                      <svg className="panel-platform-dropdown__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color }}>
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Mobile-only: current firm + store info */}
        {mobileSidebarOpen && (() => {
          const firmName = (user as any)?.firm?.name || ''
          const currentStore = ((user as any)?.firmStores || []).find((s: any) => s.id === user?.firm_store_id)
          const storeName = currentStore?.name || currentStore?.code || ''
          if (!firmName && !storeName) return null
          const initials = firmName.slice(0, 2).toUpperCase() || '?'
          return (
            <div className="panel-sidebar__mobile-firm">
              <div className="panel-sidebar__mobile-firm-avatar">{initials}</div>
              <div className="panel-sidebar__mobile-firm-info">
                <div className="panel-sidebar__mobile-firm-name">{firmName}</div>
                {storeName && <div className="panel-sidebar__mobile-firm-store">{storeName}</div>}
              </div>
            </div>
          )
        })()}

        {/* Main Navigation */}
        <nav className="panel-sidebar__nav">
          {navItems.map((item) => (
            <Link prefetch={false} 
              key={item.id} 
              href={item.href}
              className={`panel-nav-item ${pathname === item.href ? 'panel-nav-item--active' : ''}`}
              onMouseEnter={(e) => handleMouseEnter(e, item.label)}
              onMouseLeave={handleMouseLeave}
            >
              <span className="panel-nav-item__icon">{item.icon}</span>
              <span className="panel-nav-item__text">{item.label}</span>
              {renderTaskMarker(taskCountForUrl(item.href))}
            </Link>
          ))}

          {/* API'den Gelen Menü - Sadece Ana Menüler */}
          {menuItems.length > 0 && (
            <div className="panel-nav-section">
              {menuItems.map((item) => (
                <Link prefetch={false} 
                  key={item.id}
                  href={item.url || '#'}
                  className={`panel-nav-item ${activeMenuIds.includes(item.id) ? 'panel-nav-item--active' : ''}`}
                  onMouseEnter={(e) => handleMouseEnter(e, item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="panel-nav-item__icon">{renderIcon(item.icon)}</span>
                  <span className="panel-nav-item__text">{item.name}</span>
                  {renderTaskMarker(taskCountForUrl(item.url))}
                </Link>
              ))}
            </div>
          )}

          {/* Products Section */}
          {productItems && productItems.length > 0 && (
            <div className="panel-nav-section">
              <div className="panel-nav-section__title">PRODUCTS</div>
              {productItems.map((item) => (
                <Link prefetch={false} 
                  key={item.id} 
                  href={item.href}
                  className={`panel-nav-item ${pathname === item.href ? 'panel-nav-item--active' : ''}`}
                >
                  <span className="panel-nav-item__icon">{item.icon}</span>
                  <span className="panel-nav-item__text">{item.label}</span>
                </Link>
              ))}
            </div>
          )}
        </nav>

        {/* Footer Navigation */}
        <div className="panel-sidebar__footer">
          {isAdminUser && (
            <>
              <button
                type="button"
                className="panel-sidebar__tour-start"
                onClick={handleTourStart}
                onMouseEnter={(e) => handleMouseEnter(e, 'Tura Başla')}
                onMouseLeave={handleMouseLeave}
                aria-label="Panel turunu başlat"
              >
                <span className="panel-sidebar__tour-start-icon">
                  <i className="fa-regular fa-compass" />
                </span>
                <span className="panel-sidebar__tour-start-text">Tura Başla</span>
              </button>

              <button
                type="button"
                className="panel-sidebar__setup-progress"
                onClick={handleSetupGuideOpen}
                onMouseEnter={(e) => handleMouseEnter(e, 'Kuruluma başla')}
                onMouseLeave={handleMouseLeave}
                aria-label={`Kurulum ilerlemesi: ${setupSidebarCurrentStep}/${setupSidebarTotalSteps}`}
                data-onboarding-target="setup-start"
              >
                <span className="panel-sidebar__setup-progress-icon">
                  <i className="fa-regular fa-rocket" />
                </span>
                <span className="panel-sidebar__setup-progress-content">
                  <span className="panel-sidebar__setup-progress-title">Kuruluma başla</span>
                  <span className="panel-sidebar__setup-progress-row">
                    <strong>{setupSidebarCurrentStep}/{setupSidebarTotalSteps}</strong>
                    <span className="panel-sidebar__setup-progress-track">
                      <span style={{ width: `${setupSidebarProgress}%` }} />
                    </span>
                  </span>
                </span>
              </button>
            </>
          )}

          {(isFarktorDeveloper || isFarktorSupport) && (
            <div className="panel-sidebar__footer-group panel-sidebar__footer-group--staff">
              {isFarktorDeveloper && (
              <Link prefetch={false}
                href="/pages/admin/tasks"
                className={`panel-nav-item work-task-footer-link ${pathname === '/pages/admin/tasks' ? 'panel-nav-item--active' : ''}`}
                onMouseEnter={(e) => handleMouseEnter(e, 'İş Emirleri')}
                onMouseLeave={handleMouseLeave}
              >
                <span className="panel-nav-item__icon">
                  <i className="fa-duotone fa-list-check" />
                </span>
                <span className="panel-nav-item__text">İş Emirleri</span>
                {workTaskBadgeCount > 0 && <span className="panel-nav-item__alert-badge">{workTaskBadgeCount > 99 ? '99+' : workTaskBadgeCount}</span>}
              </Link>
              )}
              {isFarktorSupport && (
              <>
              <Link prefetch={false}
                href="/pages/admin/test-requests"
                className={`panel-nav-item work-task-footer-link ${(pathname === '/pages/admin/test-requests' || pathname === '/test-talepleri') ? 'panel-nav-item--active' : ''}`}
                onMouseEnter={(e) => handleMouseEnter(e, 'Test Talepleri')}
                onMouseLeave={handleMouseLeave}
              >
                <span className="panel-nav-item__icon">
                  <i className="fa-regular fa-clipboard-check" />
                </span>
                <span className="panel-nav-item__text">Test Talepleri</span>
                {testRequestBadgeCount > 0 && <span className="panel-nav-item__alert-badge">{testRequestBadgeCount > 99 ? '99+' : testRequestBadgeCount}</span>}
              </Link>
              <Link prefetch={false}
                href="/pages/admin/module-info-requests"
                className={`panel-nav-item work-task-footer-link ${pathname === '/pages/admin/module-info-requests' ? 'panel-nav-item--active' : ''}`}
                onMouseEnter={(e) => handleMouseEnter(e, 'Detaylı Bilgi Talepleri')}
                onMouseLeave={handleMouseLeave}
              >
                <span className="panel-nav-item__icon">
                  <i className="fa-duotone fa-circle-info" />
                </span>
                <span className="panel-nav-item__text">Detaylı Bilgi Talepleri</span>
                {infoRequestBadgeCount > 0 && <span className="panel-nav-item__alert-badge">{infoRequestBadgeCount > 99 ? '99+' : infoRequestBadgeCount}</span>}
              </Link>
              </>
              )}
            </div>
          )}

          <div className="panel-sidebar__footer-group panel-sidebar__footer-group--common">
            {/* Önizleme Butonu */}
            {(user as any)?.store?.domain && (
              <a
                href={(user as any).store.domain.startsWith('http') ? (user as any).store.domain : `https://${(user as any).store.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="panel-nav-item"
                onMouseEnter={(e) => handleMouseEnter(e, 'Siteye Git')}
                onMouseLeave={handleMouseLeave}
                data-onboarding-target="site-preview-link"
                style={{ textDecoration: 'none' }}
              >
                <span className="panel-nav-item__icon">
                  <i className="fa-duotone fa-eye" />
                </span>
                <span className="panel-nav-item__text">Siteye Git</span>
              </a>
            )}

            {isAdminUser && (
              <>
                {/* Not Defteri Button */}
                <button
                  onClick={() => setIsNotesOpen(!isNotesOpen)}
                  className={`panel-nav-item ${isNotesOpen ? 'panel-nav-item--active' : ''}`}
                  onMouseEnter={(e) => handleMouseEnter(e, 'Not Defteri')}
                  onMouseLeave={handleMouseLeave}
                  style={{ width: 'calc(100% - 1rem)', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span className="panel-nav-item__icon">
                    <i className="fa-duotone fa-pen-to-square" />
                  </span>
                  <span className="panel-nav-item__text">Not Defteri</span>
                </button>
                
                {/* Ayarlar Link */}
                <Link prefetch={false} 
                  href="/settings"
                  className={`panel-nav-item ${pathname === '/settings' ? 'panel-nav-item--active' : ''}`}
                  onMouseEnter={(e) => handleMouseEnter(e, 'Ayarlar')}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="panel-nav-item__icon">
                    <i className="fa-duotone fa-gear" />
                  </span>
                  <span className="panel-nav-item__text">Ayarlar</span>
                </Link>
              </>
            )}
          </div>
        </div>
        
      </aside>

      {/* Tooltip (portal to body so it stacks above submenu) */}
      {isClient && tooltip &&
        createPortal(
          <>
            <div
              style={{
                position: 'fixed',
                left: `${tooltip.x - 6}px`,
                top: `${tooltip.y}px`,
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderRight: '6px solid #000',
                zIndex: 50000,
                pointerEvents: 'none'
              }}
            />
            <div
              style={{
                position: 'fixed',
                left: `${tooltip.x}px`,
                top: `${tooltip.y}px`,
                transform: 'translateY(-50%)',
                padding: '6px 12px',
                background: '#000',
                color: 'white',
                fontSize: '11px',
                fontWeight: '600',
                whiteSpace: 'nowrap',
                borderRadius: '0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                zIndex: 50000,
                pointerEvents: 'none',
                animation: 'tooltipFadeIn 0.15s ease-out'
              }}
            >
              {tooltip.text}
            </div>
          </>,
          document.body
        )}

      {/* Notes Panel - Mac Notes tarzı */}
      {isNotesOpen && (
        <div 
          style={{
            position: 'fixed',
            left: collapsed ? '60px' : '220px',
            top: 0,
            bottom: 0,
            width: notesPanelWidth,
            background: '#fafafa',
            borderRight: '1px solid #e5e5e5',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
            animation: 'slideIn 0.2s ease-out'
          }}
        >
          {/* Notes Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e5e5e5',
            background: '#fff'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                {showTrash ? '🗑️ Çöp Kutusu' : 'Not Defteri'}
              </h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {!showTrash && (
                  <button onClick={handleCreateNote} style={{ width: '28px', height: '28px', border: 'none', background: '#007AFF', color: 'white', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }} title="Yeni Not">+</button>
                )}
                <button onClick={() => { setShowTrash(!showTrash); setSelectedNote(null); setEditingNote(null); }} style={{ width: '28px', height: '28px', border: 'none', background: showTrash ? '#ff3b30' : '#f0f0f0', color: showTrash ? 'white' : '#666', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }} title={showTrash ? 'Notlara Dön' : 'Çöp Kutusu'}>🗑️</button>
                <button onClick={() => setIsNotesOpen(false)} style={{ width: '28px', height: '28px', border: 'none', background: '#f0f0f0', color: '#666', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }} title="Kapat">✕</button>
              </div>
            </div>
            {/* Search Bar */}
            {!showTrash && (
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Notlarda ara..."
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e5e5', borderRadius: '8px', fontSize: '12px', outline: 'none', background: '#f8f8f8' }}
              />
            )}
            {showTrash && notes.filter(n => n.deleted).length > 0 && (
              <button onClick={handleEmptyTrash} style={{ width: '100%', padding: '8px', border: 'none', background: '#ff3b30', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>Çöp Kutusunu Boşalt</button>
            )}
          </div>

          {/* Notes List & Editor Container */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Notes List */}
            <div style={{ width: selectedNote ? '140px' : '100%', minWidth: selectedNote ? '140px' : 'auto', borderRight: selectedNote ? '1px solid #e5e5e5' : 'none', overflowY: 'auto', background: '#fafafa' }}>
              {filteredNotes.length === 0 ? (
                <div style={{ padding: '24px 16px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                  {showTrash ? 'Çöp kutusu boş' : (searchQuery ? 'Sonuç bulunamadı' : 'Henüz not yok')}
                </div>
              ) : (
                filteredNotes.map(note => (
                  <div
                    key={note.id}
                    onClick={() => handleSelectNote(note)}
                    style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid #ebebeb',
                      cursor: 'pointer',
                      background: selectedNote?.id === note.id ? '#fff' : note.color || '#fafafa',
                      borderLeft: selectedNote?.id === note.id ? '3px solid #007AFF' : '3px solid transparent',
                      position: 'relative'
                    }}
                  >
                    {/* Pin icon */}
                    {note.pinned && !showTrash && (
                      <span style={{ position: 'absolute', top: '6px', right: '6px', fontSize: '10px' }}>📌</span>
                    )}
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: note.pinned ? '16px' : '0' }}>
                      {note.title || 'Başlıksız'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {note.content.substring(0, 40) || 'Boş not...'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '10px', color: '#999' }}>{formatDate(note.updatedAt)}</span>
                        
                        {note.sharedWith.length > 0 && (
                          <span style={{ fontSize: '9px', color: '#34c759' }}>👥{note.sharedWith.length}</span>
                        )}
                        {note.attachments.length > 0 && (
                          <span style={{ fontSize: '9px', color: '#ff9500' }}>📎{note.attachments.length}</span>
                        )}
                      </div>
                      {!selectedNote && !showTrash && (
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }} style={{ border: 'none', background: 'transparent', color: '#999', cursor: 'pointer', fontSize: '10px', padding: '2px 4px' }} title="Sil">🗑️</button>
                      )}
                      {showTrash && (
                        <button onClick={(e) => { e.stopPropagation(); handleRestoreNote(note.id); }} style={{ border: 'none', background: '#007AFF', color: 'white', cursor: 'pointer', fontSize: '10px', padding: '2px 6px', borderRadius: '4px' }} title="Geri Yükle">↩️</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Note Editor */}
            {selectedNote && editingNote && !showTrash && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: selectedNote.color || '#fff', overflow: 'hidden' }}>
                {/* Editor Toolbar */}
                <div style={{ padding: '8px 12px', borderBottom: '1px solid #ebebeb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => handleTogglePin(selectedNote.id)} style={{ border: 'none', background: selectedNote.pinned ? '#007AFF' : '#f0f0f0', color: selectedNote.pinned ? 'white' : '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px' }} title={selectedNote.pinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}>📌</button>
                    <button onClick={insertCheckbox} style={{ border: 'none', background: '#f0f0f0', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px' }} title="Checkbox Ekle">☑️</button>
                    <div style={{ position: 'relative' }}>
                      <button onClick={() => setShowColorPicker(!showColorPicker)} style={{ border: 'none', background: '#f0f0f0', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: selectedNote.color, border: '1px solid #ddd' }}></span>
                      </button>
                      {showColorPicker && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: 'white', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap', width: '120px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 100 }}>
                          {noteColors.map(c => (
                            <button key={c.value} onClick={() => handleChangeColor(selectedNote.id, c.value)} style={{ width: '20px', height: '20px', borderRadius: '4px', border: selectedNote.color === c.value ? '2px solid #007AFF' : '1px solid #ddd', background: c.value, cursor: 'pointer' }} title={c.name} />
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setShowShareModal(!showShareModal)} style={{ border: 'none', background: selectedNote.sharedWith.length > 0 ? '#34c759' : '#f0f0f0', color: selectedNote.sharedWith.length > 0 ? 'white' : '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px' }} title="Paylaş">👥</button>
                    <label style={{ border: 'none', background: '#f0f0f0', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center' }} title="Dosya Ekle">
                      📎
                      <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                    </label>
                    <button onClick={handleTakeScreenshot} style={{ border: 'none', background: '#f0f0f0', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px' }} title="Ekran Görüntüsü">📷</button>
                    <button onClick={isRecording ? handleStopRecording : handleStartRecording} style={{ border: 'none', background: isRecording ? '#ff3b30' : '#f0f0f0', color: isRecording ? 'white' : '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px', animation: isRecording ? 'pulse 1s infinite' : 'none' }} title={isRecording ? 'Kaydı Durdur' : 'Ses Kaydet'}>🎙️ {isRecording && formatDuration(recordingTime)}</button>
                    <button onClick={() => handleExportNote(selectedNote)} style={{ border: 'none', background: '#f0f0f0', color: '#666', cursor: 'pointer', fontSize: '10px', padding: '4px 6px', borderRadius: '4px' }} title="Dışa Aktar">📤</button>
                  </div>
                  <button onClick={() => handleDeleteNote(selectedNote.id)} style={{ border: 'none', background: 'transparent', color: '#ff3b30', cursor: 'pointer', fontSize: '12px', padding: '4px' }} title="Sil">🗑️</button>
                </div>
                
                {/* Share Modal */}
                {showShareModal && (
                  <div style={{ padding: '12px', borderBottom: '1px solid #ebebeb', background: 'white' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: '#1a1a1a' }}>Paylaş</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {teamMembers.map(user => {
                        const isSelected = selectedNote.sharedWith.some(u => u.id === user.id)
                        return (
                          <button key={user.id} onClick={() => handleToggleShare(user)} style={{ padding: '4px 8px', border: isSelected ? '2px solid #007AFF' : '1px solid #ddd', background: isSelected ? '#e3f2fd' : 'white', borderRadius: '12px', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#007AFF', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px' }}>{user.name.charAt(0)}</span>
                            {user.name.split(' ')[0]}
                            {isSelected && <span>✓</span>}
                          </button>
                        )
                      })}
                    </div>
                    {selectedNote.sharedWith.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                        {selectedNote.sharedWith.length} kişiyle paylaşıldı
                      </div>
                    )}
                  </div>
                )}
                
                {/* Content Editable Area */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <textarea 
                    data-notes-editor="true"
                    value={editingNote ? editingNote.title + '\n' + editingNote.content : ''} 
                    onChange={(e) => { 
                      const val = e.target.value
                      const lines = val.split('\n')
                      setEditingNote({ title: lines[0] || '', content: lines.slice(1).join('\n') })
                    }} 
                    onKeyDown={handleEditorKeyDown}
                    onBlur={handleSaveNote} 
                    placeholder="Başlık için ilk satırı yazın...\n\nNotunuza devam edin..." 
                    style={{ 
                      width: '100%', 
                      flex: 1, 
                      border: 'none', 
                      outline: 'none', 
                      fontSize: '13px', 
                      resize: 'none', 
                      color: '#333', 
                      lineHeight: '1.6', 
                      background: 'transparent', 
                      fontFamily: 'inherit', 
                      padding: '12px'
                    }} 
                  />
                  
                  {/* Attachments & Todos at bottom */}
                  <div style={{ padding: '12px', borderTop: '1px solid #ebebeb', overflowY: 'auto', maxHeight: '200px' }}>
                  
                    {/* Attachments Section */}
                  {selectedNote.attachments.length > 0 && (
                    <div style={{ marginTop: '12px', borderTop: '1px solid #ebebeb', paddingTop: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: '#1a1a1a' }}>📎 Ekler ({selectedNote.attachments.length})</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {selectedNote.attachments.map(att => (
                          <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'rgba(0,0,0,0.03)', borderRadius: '6px' }}>
                            <span style={{ fontSize: '14px' }}>{att.type === 'voice' ? '🎙️' : att.type === 'image' ? '🖼️' : '📄'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '11px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</div>
                              <div style={{ fontSize: '9px', color: '#999' }}>
                                {att.type === 'voice' && att.duration ? formatDuration(att.duration) : att.size ? formatFileSize(att.size) : ''}
                              </div>
                            </div>
                            <button onClick={() => handleDeleteAttachment(att.id)} style={{ border: 'none', background: 'transparent', color: '#999', cursor: 'pointer', fontSize: '10px' }}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </div>
                </div>
                
                {/* Footer - Date & Shared Info */}
                <div style={{ padding: '8px 12px', borderTop: '1px solid #ebebeb', background: 'rgba(255,255,255,0.5)', fontSize: '10px', color: '#999', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span>{formatDate(selectedNote.updatedAt)}</span>
                  {selectedNote.sharedWith.length > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      👥 {selectedNote.sharedWith.map(u => u.name.split(' ')[0]).join(', ')}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Trash View - Selected Note */}
            {selectedNote && showTrash && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', padding: '16px' }}>
                <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#1a1a1a' }}>{selectedNote.title}</h4>
                <p style={{ fontSize: '12px', color: '#666', flex: 1, whiteSpace: 'pre-wrap' }}>{selectedNote.content}</p>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button onClick={() => handleRestoreNote(selectedNote.id)} style={{ flex: 1, padding: '8px', border: 'none', background: '#007AFF', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Geri Yükle</button>
                  <button onClick={() => handlePermanentDelete(selectedNote.id)} style={{ flex: 1, padding: '8px', border: 'none', background: '#ff3b30', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Kalıcı Sil</button>
                </div>
              </div>
            )}
          </div>
          
          {/* Resize Handle */}
          <div onMouseDown={handleResizeStart} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'ew-resize', background: isResizing ? 'rgba(0,122,255,0.3)' : 'transparent', transition: 'background 0.15s', zIndex: 1000 }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,122,255,0.2)'} onMouseLeave={(e) => !isResizing && (e.currentTarget.style.background = 'transparent')} />
        </div>
      )}

      {/* Animation styles */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
      <DemoOnboardingTour user={user} startSignal={tourStartSignal} />
    </>
  )
}

// Sol panel için aktif menü bilgilerini döndüren yardımcı fonksiyon
export function getLeftPanelData(pathname: string, nav: NavItem[]) {
  const findAllParentIds = (menuId: number): number[] => {
    const menu = nav.find(item => item.id === menuId)
    if (!menu || !menu.top_id) return []
    return [menu.top_id, ...findAllParentIds(menu.top_id)]
  }

  const getActiveMenuIds = () => {
    const activeIds: number[] = []
    const pathAliases = getPanelPathAliases(pathname)
    
    const matchingMenus = nav
      .filter(item => {
        if (!item.url || item.url === '/') return false
        const menuUrl = normalizeNavPath(item.url)
        for (const pathAlias of pathAliases) {
          if (menuUrl === pathAlias) return true
          if (pathAlias.startsWith(`${menuUrl}/`)) return true
        }
        return false
      })
      .sort((a, b) => (b.url?.length || 0) - (a.url?.length || 0))
    
    const matchedMenu = matchingMenus[0]
    if (matchedMenu) {
      activeIds.push(matchedMenu.id)
      const parentIds = findAllParentIds(matchedMenu.id)
      activeIds.push(...parentIds)
    }
    
    return activeIds
  }

  const activeMenuIds = getActiveMenuIds()
  
  const getActiveParentMenu = (): NavItem | null => {
    if (activeMenuIds.length === 0) return null
    const activeMenu = nav.find(item => activeMenuIds.includes(item.id))
    if (!activeMenu) return null
    
    let currentMenu = activeMenu
    while (currentMenu.top_id && currentMenu.top_id !== 0) {
      const parent = nav.find(item => item.id === currentMenu.top_id)
      if (!parent) break
      currentMenu = parent
    }
    
    return currentMenu
  }
  
  const activeParentMenu = getActiveParentMenu()
  
  const subMenus = activeParentMenu 
    ? nav.filter(item => item.top_id === activeParentMenu.id && item.nav === 1) 
    : []
  
  return {
    parentMenu: activeParentMenu,
    subMenus,
    activeMenuIds
  }
}
